import { globalRef, type Maybe } from '@amazon/vinyl-util'
import { PRERENDER_NOT_FOUND_ATTR } from '@amzn/prerender-contract'
import { ErrorSurface } from '@/components/ErrorSurface'
import { getString } from '@/notFound/i18n/bundle.generated'
import { describeRoutedPage, type MetricsPageType } from '@/metrics/uiMetrics'

/**
 * Content-not-found page — mirrors green hornet's ErrorContentNotFound
 * (warning icon, title, glass Home and Search buttons). Rendered as the router
 * fallback for unknown paths and inlined by detail-page loaders that used to
 * redirect to `/` on an empty wire response.
 */
export function NotFoundPage() {
    const surface = NotFoundSurface()
    surface.setAttribute(PRERENDER_NOT_FOUND_ATTR, '')
    return surface
}

/** The not-found content as a section of a page that is otherwise valid, so prerender keeps the page's status. */
export function NotFoundSurface() {
    return ErrorSurface({
        centered: true,
        message: getString('dmusic_not_found_primary_text'),
        actions: [
            { href: '/', label: getString('dmusic_not_found_secondary_text_link') },
            { href: '/search', label: getString('dmusic_not_found_search_link') },
        ],
    })
}

/** Where a feature has not launched in the customer's territory: GH's FeatureUnavailablePage, reported as not found. */
export function FeatureUnavailablePage(pageSubType: string): HTMLElement {
    const surface = ErrorSurface({
        centered: true,
        icon: 'globe',
        message: getString('dmusic_feature_unavailable_territory_primary_text'),
        detail: getString('dmusic_feature_unavailable_territory_secondary_text'),
        actions: [{ href: '/', label: getString('dmusic_feature_unavailable_home_link') }],
    })
    surface.setAttribute(PRERENDER_NOT_FOUND_ATTR, '')
    return markWithheldRouteView(surface, { pageSubType })
}

const withheldRouteViewsRef = globalRef(() => new WeakMap<Element, WithheldRouteDetails>())

export interface WithheldRouteDetails {
    readonly pageSubType?: string
    /** Report the withheld route's own page type as the sub-type, as GH's gated pages do. */
    readonly routePageAsSubType?: true
}

/** Marks a view standing in for a route this session withholds, so it reports as the not-found page. */
export function markWithheldRouteView<T extends Element>(
    view: T,
    details: WithheldRouteDetails = {}
): T {
    withheldRouteViewsRef.value.set(view, details)
    return view
}

/** The not-found page in place of a route this session withholds. */
export function WithheldRoutePage(details?: WithheldRouteDetails): HTMLElement {
    return markWithheldRouteView(NotFoundPage(), details)
}

/** The not-found page replaced the routed `page` in place: GH's NotFoundPage reports its own view instead of the page's. */
export function reportInPageNotFound(page: Element, pageSubType: MetricsPageType): void {
    describeRoutedPage(page, { pageType: 'NOT_FOUND', pageSubType })
}

export function withheldRouteDetails(view: Element): Maybe<WithheldRouteDetails> {
    return withheldRouteViewsRef.value.get(view)
}
